// ============================================================================
// BASE ENDPOINTS
// ============================================================================
// Base endpoint utilities and helpers

import { apiConfig } from '../config/apiConfig';

/**
 * Get API version prefix
 */
export const getVersionPrefix = (): string => {
  return apiConfig.apiVersion ? `/${apiConfig.apiVersion}` : '';
};

/**
 * Build endpoint URL with version
 */
export const buildEndpoint = (path: string): string => {
  const versionPrefix = getVersionPrefix();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${versionPrefix}${cleanPath}`;
};

/**
 * Build endpoint with path parameters
 * @example buildEndpointWithParams('/users/:id', { id: 123 }) => '/users/123'
 */
export const buildEndpointWithParams = (
  path: string,
  params: Record<string, string | number>
): string => {
  let endpoint = path;

  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`:${key}`, String(value));
  });

  return buildEndpoint(endpoint);
};

/**
 * Build endpoint with query parameters
 * @example buildEndpointWithQuery('/users', { page: 1, limit: 10 }) => '/users?page=1&limit=10'
 */
export const buildEndpointWithQuery = (
  path: string,
  query: Record<string, any>
): string => {
  const endpoint = buildEndpoint(path);
  const queryString = new URLSearchParams(
    Object.entries(query).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = String(value);
      }
      return acc;
    }, {} as Record<string, string>)
  ).toString();

  return queryString ? `${endpoint}?${queryString}` : endpoint;
};

/**
 * Common endpoint patterns
 */
export const ENDPOINT_PATTERNS = {
  LIST: '',
  DETAIL: '/:id',
  CREATE: '',
  UPDATE: '/:id',
  DELETE: '/:id',
  SEARCH: '/search',
  BULK: '/bulk',
  EXPORT: '/export',
  IMPORT: '/import',
} as const;
